import { useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { useSession } from "../../lib/session.js";
import { formatDate, timeAgo } from "../../lib/format.js";
import { AdminTabs } from "../../components/AdminTabs.js";
import { PageSpinner, RoleBadge, StatusBadge } from "../../components/ui.js";

interface AdminUser {
  id: string;
  username: string;
  displayName: string | null;
  email: string | null;
  role: "admin" | "member" | "trainer";
  status: "active" | "disabled";
  createdAt: string;
  lastLoginAt: string | null;
}

export function AdminUsersPage() {
  const { user: me } = useSession();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    try {
      const data = await api.get<{ users: AdminUser[] }>("/api/admin/users");
      setUsers(data.users);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load users.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function setStatus(id: string, status: "active" | "disabled") {
    setBusyId(id);
    setError(null);
    try {
      await api.post(`/api/admin/users/${id}/status`, { status });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Action failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function setRole(id: string, role: string) {
    setBusyId(id);
    setError(null);
    try {
      await api.post(`/api/admin/users/${id}/role`, { role });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Action failed.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1>Administration</h1>
        <p className="subtitle">People with access to this ClovaForm.</p>
      </div>
      <AdminTabs />

      {error && <div className="alert alert-danger">{error}</div>}
      {!users ? (
        <PageSpinner />
      ) : (
        <>
          <div className="data-table-wrap card" style={{ padding: 0 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Last login</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{u.displayName ?? u.username}</div>
                      <div className="dim mono text-xs">@{u.username}</div>
                    </td>
                    <td>
                      <select
                        className="select"
                        style={{ minHeight: 36, maxWidth: 130 }}
                        value={u.role}
                        disabled={busyId === u.id || u.id === me?.id}
                        onChange={(e) => setRole(u.id, e.target.value)}
                      >
                        <option value="admin">admin</option>
                        <option value="member">member</option>
                        <option value="trainer">trainer</option>
                      </select>
                    </td>
                    <td>
                      <StatusBadge status={u.status} />
                    </td>
                    <td className="dim">{formatDate(u.createdAt)}</td>
                    <td className="dim">{timeAgo(u.lastLoginAt)}</td>
                    <td style={{ textAlign: "right" }}>
                      {u.id !== me?.id &&
                        (u.status === "active" ? (
                          <button
                            className="btn btn-sm btn-danger"
                            disabled={busyId === u.id}
                            onClick={() => setStatus(u.id, "disabled")}
                          >
                            Disable
                          </button>
                        ) : (
                          <button
                            className="btn btn-sm"
                            disabled={busyId === u.id}
                            onClick={() => setStatus(u.id, "active")}
                          >
                            Enable
                          </button>
                        ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="table-cards">
            {users.map((u) => (
              <div className="table-card" key={u.id}>
                <div className="row-between">
                  <div>
                    <div style={{ fontWeight: 600 }}>{u.displayName ?? u.username}</div>
                    <div className="dim mono text-xs">@{u.username}</div>
                  </div>
                  <StatusBadge status={u.status} />
                </div>
                <div className="tc-row">
                  <span className="tc-label">Role</span>
                  <RoleBadge role={u.role} />
                </div>
                <div className="tc-row">
                  <span className="tc-label">Last login</span>
                  <span>{timeAgo(u.lastLoginAt)}</span>
                </div>
                {u.id !== me?.id && (
                  <div className="row mt-4 wrap">
                    <select
                      className="select"
                      style={{ minHeight: 40, flex: 1 }}
                      value={u.role}
                      disabled={busyId === u.id}
                      onChange={(e) => setRole(u.id, e.target.value)}
                    >
                      <option value="admin">admin</option>
                      <option value="member">member</option>
                      <option value="trainer">trainer</option>
                    </select>
                    {u.status === "active" ? (
                      <button
                        className="btn btn-sm btn-danger"
                        disabled={busyId === u.id}
                        onClick={() => setStatus(u.id, "disabled")}
                      >
                        Disable
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm"
                        disabled={busyId === u.id}
                        onClick={() => setStatus(u.id, "active")}
                      >
                        Enable
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
